import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import { ArrowRightOutlined } from "@/config/antd";
import {
  setCurrentStep,
  updateStep,
  addPermission,
  removePermission,
  selectRoleFormData,
} from "@/store/redux/slices/roles/role_slice";
import Button from "@/components/common/Button";
import Back from "@/components/specific/Back";
import Collapse from "@/components/specific/Collapse";
import { mapSelectedStatesToPermissions, PERMISSION_MAPPINGS } from "./permissionMap";

const AddRolesStep2 = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formData = useSelector(selectRoleFormData);
  const { id } = useParams();

  const isEditMode = Boolean(id);

  const isRolePrivilegesSelected = true;

  const getInitialSelectedItems = (category) => {
    const mapping = PERMISSION_MAPPINGS[category] || {};
    const permissions = formData.permissions || [];
    const initial = {};
    Object.entries(mapping).forEach(([index, permissionKey]) => {
      initial[index] = permissions.includes(permissionKey);
    });
    return initial;
  };

  const handleBack = () => {
    dispatch(setCurrentStep(1));
    navigate(
      isEditMode
        ? `/dashboard/roles/edit/${id}/step-1`
        : "/dashboard/roles/add-roles-step-1"
    );
  };

  const handlePrivilegeSelect = (selectedData, category) => {
    const selectedStates = { ...selectedData };
    delete selectedStates.itemText;
    const selectedPrivileges = mapSelectedStatesToPermissions(
      selectedStates,
      category
    );
    const categoryPermissions = Object.values(PERMISSION_MAPPINGS[category] || {});

    categoryPermissions.forEach((permission) => {
      dispatch(removePermission(permission));
    });

    selectedPrivileges.forEach((permission) => {
      dispatch(addPermission(permission));
    });
  };

  const handleProceed = () => {
    showLoader();

    dispatch(
      updateStep({
        step: "step2",
        data: {
          privileges: formData.permissions || [],
        },
      })
    );

    dispatch(setCurrentStep(3));

    setTimeout(() => {
      hideLoader();
      navigate(
        isEditMode
          ? `/dashboard/roles/edit/${id}/step-3`
          : "/dashboard/roles/add-roles-step-3"
      );
    }, 2000);
  };

  return (
    <div className="py-8">
      <div className="flex flex-col gap-y-5 mb-5">
        <Back />
      </div>
      <div className="flex-row-reverse gap-40 md:flex">
        <div className="w-full lg:border-2 border-[#eee] lg:p-3 rounded-lg">
          <h1 className="mb-2 text-left heading-medium sm:text-3xl">
            {isEditMode ? "Edit Role Privileges" : "Create a New Role"}
          </h1>
          <p className="mb-6 text-left text-small">
            Select all applicable Privileges that will be associated with this
            role
          </p>

          <div className="w-full">
            {/* User Management */}
            <Collapse
              title="User Management Permissions"
              subtitle="Permission holder can manage users"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "User Management")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("User Management")}>
              <div>
                <h1 className="font-bold text-small">Create Users</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can add new users to the system.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Users</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify user information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Users</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view user information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Users</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove users from the system.
                </p>
              </div>
            </Collapse>

            {/* Role Management */}
            <Collapse
              title="Role Management Permissions"
              subtitle="Permission holder can manage roles"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Role Management")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Role Management")}>
              <div>
                <h1 className="font-bold text-small">Create Roles</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new roles.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Roles</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify existing roles.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Roles</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view role information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Roles</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove roles from the system.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Create Role Assignments
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can assign roles to users.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Update Role Assignments
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify role assignments.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Role Assignments</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view role assignments.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Delete Role Assignments
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove role assignments.
                </p>
              </div>
            </Collapse>

            {/* Organization & Account */}
            <Collapse
              title="Organization & Account Permissions"
              subtitle="Permission holder can manage organizations and accounts"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Organization & Account")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Organization & Account")}>
              <div>
                <h1 className="font-bold text-small">Create Organizations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new organizations.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Organizations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify organization information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Organizations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view organization information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Organizations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove organizations.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Accounts</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new accounts.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Accounts</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify account information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Accounts</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view account information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Accounts</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove accounts.
                </p>
              </div>
            </Collapse>

            {/* Authentication & Security */}
            <Collapse
              title="Authentication & Security Permissions"
              subtitle="Permission holder can manage authentication methods and security settings"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Authentication & Security")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Authentication & Security")}>
              <div>
                <h1 className="font-bold text-small">Create Authenticators</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new authentication methods.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Authenticators</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify authentication methods.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Authenticators</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view authentication methods.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Authenticators</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove authentication methods.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create OTP</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create OTP settings.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update OTP</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify OTP settings.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read OTP</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view OTP information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete OTP</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove OTP settings.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Create Recovery Questions
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create recovery questions.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Update Recovery Questions
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify recovery questions.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Read Recovery Questions
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view recovery questions.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">
                  Delete Recovery Questions
                </h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove recovery questions.
                </p>
              </div>
            </Collapse>

            {/* Event Management */}
            <Collapse
              title="Event Management Permissions"
              subtitle="Permission holder can manage events, sub-events, and townhalls"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Event Management")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Event Management")}>
              <div>
                <h1 className="font-bold text-small">Create Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new events.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify event information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view event information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove events.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Sub-Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new sub-events.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Sub-Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify sub-event information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Sub-Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view sub-event information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Sub-Events</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove sub-events.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Townhalls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new townhalls.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Townhalls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify townhall information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Townhalls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view townhall information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Townhalls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove townhalls.
                </p>
              </div>
            </Collapse>

            {/* Financial Management */}
            <Collapse
              title="Financial Management Permissions"
              subtitle="Permission holder can manage expenses, campaign funds, and donations"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Financial Management")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Financial Management")}>
              <div>
                <h1 className="font-bold text-small">Create Expenses</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new expenses.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Expenses</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify expense information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Expenses</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view expense information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Expenses</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove expenses.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Campaign Funds</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new campaign funds.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Campaign Funds</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify campaign fund information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Campaign Funds</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view campaign fund information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Campaign Funds</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove campaign funds.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Donations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new donations.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Donations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify donation information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Donations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view donation information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Donations</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove donations.
                </p>
              </div>
            </Collapse>

            {/* Communication & Media */}
            <Collapse
              title="Communication & Media Permissions"
              subtitle="Permission holder can manage surveys, polls, and media"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Communication & Media")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Communication & Media")}>
              <div>
                <h1 className="font-bold text-small">Create Surveys/Polls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new surveys and polls.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Surveys/Polls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify survey and poll information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Surveys/Polls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view survey and poll information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Surveys/Polls</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove surveys and polls.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Media</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new media.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Media</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify media information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Media</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view media information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Media</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove media.
                </p>
              </div>
            </Collapse>

            {/* Support & Content */}
            <Collapse
              title="Support & Content Permissions"
              subtitle="Permission holder can manage support tickets and FAQs"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Support & Content")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Support & Content")}>
              <div>
                <h1 className="font-bold text-small">Create Support Tickets</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new support tickets.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Support Tickets</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify support ticket information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Support Tickets</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view support ticket information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Support Tickets</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove support tickets.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create FAQs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new FAQs.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update FAQs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify FAQ information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read FAQs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view FAQ information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete FAQs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove FAQs.
                </p>
              </div>
            </Collapse>

            {/* Candidate & Campaign */}
            <Collapse
              title="Candidate & Campaign Permissions"
              subtitle="Permission holder can manage candidates and manifestos"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "Candidate & Campaign")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Candidate & Campaign")}>
              <div>
                <h1 className="font-bold text-small">Create Candidates</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new candidates.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Candidates</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify candidate information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Candidates</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view candidate information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Candidates</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove candidates.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Create Manifestos</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new manifestos.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Manifestos</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify manifesto information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Manifestos</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view manifesto information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Manifestos</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove manifestos.
                </p>
              </div>
            </Collapse>

            {/* Canvass */}
            <Collapse
              title="Canvass Permissions"
              subtitle="Permission holder can manage canvass"
              onChildSelect={(data) => handlePrivilegeSelect(data, "Canvass")}
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("Canvass")}>
              <div>
                <h1 className="font-bold text-small">Create Canvass</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create new canvass.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Canvass</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify canvass information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Canvass</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view canvass information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Canvass</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove canvass.
                </p>
              </div>
            </Collapse>

            {/* System & Logs */}
            <Collapse
              title="System & Logs Permissions"
              subtitle="Permission holder can access and manage system logs"
              onChildSelect={(data) =>
                handlePrivilegeSelect(data, "System & Logs")
              }
              showCheckbox={true}
              initialSelectedItems={getInitialSelectedItems("System & Logs")}>
              <div>
                <h1 className="font-bold text-small">Create Activity Logs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can create activity log entries.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Update Activity Logs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can modify activity log information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Read Activity Logs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can view activity log information.
                </p>
              </div>
              <div>
                <h1 className="font-bold text-small">Delete Activity Logs</h1>
                <p className="text-small dark-mode-text">
                  Permission holder can remove activity log entries.
                </p>
              </div>
            </Collapse>

            <div className="flex gap-4">
              <Button
                text="Back"
                onClick={handleBack}
                customTextColor="primary"
                customBgColor="secondary"
                className="h-[70px] flex-1"
              />
              <Button
                text={isEditMode ? "Review Update" : "Proceed"}
                icon={<ArrowRightOutlined />}
                onClick={handleProceed}
                isActive={isRolePrivilegesSelected}
                className={`h-[70px] flex-1 ${
                  isRolePrivilegesSelected
                    ? "bg-primary text-white"
                    : "bg-darkBlue text-primary"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRolesStep2;
